import { col, FLOW_ASSURANCE_FLAGS, makeTargetOutput, NETWORK_DIRECT, RESERVOIR_DIRECT, type Shape } from './base.ts';

export const FIELD_RELATION_DIRECT: Shape = [col('field', 'unitless', 'Reference to the parent field model entity.')];

/**
 * The network-grain direct-relation carried by the choke / injection-header /
 * pump network-output sources (B3): the `network` ref to the parent Network
 * model entity. NOTE the source names the grain ref `network` (NOT
 * `networkName`), so the `NetworkInputs_Agent` {@link NETWORK_RELATION} (whose
 * suffix is `networkName`) does NOT match here — these sources mirror the
 * `JointOutputs`/`SeparatorOutputs` `network` ref instead. Kept verbatim
 * (`prefix:''` → no rename).
 */
export const NETWORK_RELATION_DIRECT: Shape = [NETWORK_DIRECT];

/**
 * The network-grain entity relation carried by the choke / injection-header /
 * pump network-output sources (B3): the `entityName` ref to the equipment
 * Entity (choke, injection header, pump). Unlike Flowline's `entityName` (a
 * `z.json()` blob → unknownCol), these sources expose `entityName` as a real
 * Entity direct-relation, so it is kept verbatim alongside the `network` grain
 * ref ({@link NETWORK_RELATION_DIRECT}). Composed via spread.
 */
export const NETWORK_ENTITY_RELATION: Shape = [col('entityName', 'unitless', 'Reference to the equipment entity.')];

/**
 * The flowline-grain relations carried by `FlowlineOutputs_Network_Agent` (B3):
 * the parent `network` ref (NETWORK grain) plus the `pipelineEntity` flowline
 * ref. Both kept verbatim (`prefix:''` → no rename). NOTE the source's
 * `entityName` is a `z.json()` blob (NOT an Entity relation), which types to
 * `unknownCol`, so it is deliberately excluded here (mirrors the
 * {@link JOINT_OUTPUTS_RELATION} `entityName` exclusion).
 */
export const FLOWLINE_OUTPUTS_RELATION: Shape = [
  NETWORK_DIRECT,
  col('pipelineEntity', 'unitless', 'Reference to the flowline/pipeline entity.'),
];

/**
 * The reservoir-grain direct-relation carried by `ReservoirInputs_Agent` (B3):
 * the `reservoir` ref to the Reservoir model entity. NOTE the source names the
 * grain ref `reservoir` (NOT `reservoirEntity`), so the daily-rollup
 * {@link RESERVOIR_RELATION} (suffix `reservoirEntity`) does NOT match —
 * mirrors the {@link RESERVOIR_OUTPUTS_RELATION} precedent. Kept verbatim
 * (`prefix:''` → no rename).
 */
export const RESERVOIR_RELATION_DIRECT: Shape = [RESERVOIR_DIRECT];

/**
 * Field-grain VFM/injection allocation factors on `AllocationFactors_Field_Agent`
 * (B3): the preferred-VFM and weighted-average-VFM per-phase production
 * allocation factors plus the gas/water injection allocation factors. Bare
 * source columns — `suffix === canonical`, `prefix:''` (no de-prefix axis).
 */
export const FIELD_ALLOCATION_FACTORS: Shape = [
  col('preferredVFMOilAllocationFactor', 'unitless', 'Preferred-VFM oil production allocation factor for the field.'),
  col(
    'preferredVFMAssociatedGasAllocationFactor',
    'unitless',
    'Preferred-VFM associated-gas production allocation factor for the field.',
  ),
  col(
    'preferredVFMNonAssociatedGasAllocationFactor',
    'unitless',
    'Preferred-VFM non-associated-gas production allocation factor for the field.',
  ),
  col(
    'preferredVFMWaterAllocationFactor',
    'unitless',
    'Preferred-VFM water production allocation factor for the field.',
  ),
  col('gasInjectionAllocationFactor', 'unitless', 'Gas injection allocation factor for the field.'),
  col('waterInjectionAllocationFactor', 'unitless', 'Water injection allocation factor for the field.'),
  col(
    'weightedAverageVFMOilAllocationFactor',
    'unitless',
    'Weighted-average-VFM oil production allocation factor for the field.',
  ),
  col(
    'weightedAverageVFMAssociatedGasAllocationFactor',
    'unitless',
    'Weighted-average-VFM associated-gas production allocation factor for the field.',
  ),
  col(
    'weightedAverageVFMNonAssociatedGasAllocationFactor',
    'unitless',
    'Weighted-average-VFM non-associated-gas production allocation factor for the field.',
  ),
  col(
    'weightedAverageVFMWaterAllocationFactor',
    'unitless',
    'Weighted-average-VFM water production allocation factor for the field.',
  ),
];

/**
 * The single choke-size measure on `ChokeOutputs_Network_Agent` (B3), applied
 * with `prefix:'nmv'` to de-mark the network-matched-value marker
 * (`nmvChokeSize` → `chokeSize`).
 */
export const CHOKE_OUTPUTS: Shape = [col('chokeSize', 'chokeSize', 'Choke bean size.', { suffix: 'ChokeSize' })];

/**
 * The flowline-output measure family on `FlowlineOutputs_Network_Agent` (B3),
 * applied with `prefix:'nmv'` to de-mark the network-matched-value marker
 * (`nmvFlowlinePressure` → `flowlinePressure`, …). The oil/gas/water rates
 * de-mark to the same `oilRate`/`gasRate`/`waterRate` as {@link PHASE_RATES}, so
 * those three are NOT re-declared here — `PHASE_RATES` is reused at
 * `prefix:'nmv'` in the view. This shape carries the rest of the `nmv*` family:
 * pressures, ratios, contaminants, velocities, manifold ids, injection rates and
 * the flow-assurance / constraint flags.
 */
export const FLOWLINE_OUTPUTS: Shape = [
  col('flowlinePressure', 'pressureAbsolute', 'Simulated flowline outlet pressure.', { suffix: 'FlowlinePressure' }),
  col('measuredFlowlinePressure', 'pressureAbsolute', 'Measured flowline pressure for comparison to simulated.', {
    suffix: 'MeasuredFlowlinePressure',
  }),
  col('pressure', 'pressureAbsolute', 'Pressure at the flowline node.', { suffix: 'Pressure' }),
  col('temperature', 'temperature', 'Temperature at the flowline node.', { suffix: 'Temperature' }),
  col('dp', 'pressureDelta', 'Differential pressure across the flowline.', { suffix: 'Dp' }),
  col('liquidRate', 'liquidRate', 'Total liquid flow rate through the flowline.', { suffix: 'LiquidRate' }),
  col('injectionWaterRate', 'liquidRate', 'Water injection flow rate through the flowline.', {
    suffix: 'InjectionWaterRate',
  }),
  col('injectionGasRate', 'gasRate', 'Gas injection flow rate through the flowline.', { suffix: 'InjectionGasRate' }),
  col('gor', 'gasOilRatio', 'Gas-oil ratio.', { suffix: 'Gor' }),
  col('glr', 'gasOilRatio', 'Gas-liquid ratio.', { suffix: 'Glr' }),
  col('wgr', 'liquidGasRatio', 'Water-gas ratio.', { suffix: 'Wgr' }),
  col('cgr', 'liquidGasRatio', 'Condensate-gas ratio.', { suffix: 'Cgr' }),
  col('waterCut', 'fraction', 'Water cut fraction.', { suffix: 'WaterCut' }),
  col('cFactor', 'unitless', 'C-factor for inflow performance.', { suffix: 'CFactor' }),
  col('erosionVelocity', 'velocity', 'Erosion velocity ratio.', { suffix: 'ErosionVelocity' }),
  col('mixtureVelocity', 'velocity', 'Mixture velocity in the flowline.', { suffix: 'MixtureVelocity' }),
  col('connectedManifoldId', 'unitless', 'ID of connected production manifold/junction.', {
    suffix: 'ConnectedManifoldId',
  }),
  col('pressureMatchIndex', 'unitless', 'Pressure match quality.', { suffix: 'PressureMatchIndex' }),
  col('pressureMatchIndexFLP', 'unitless', 'Flowline-specific pressure match quality.', {
    suffix: 'PressureMatchIndexFLP',
  }),
  col('bottleneckFlag', 'unitless', 'Flowline identified as production bottleneck.', { suffix: 'BottleneckFlag' }),
  col('unstableFlag', 'unitless', 'Unstable flow detected.', { suffix: 'UnstableFlag' }),
  ...FLOW_ASSURANCE_FLAGS,
  col('violatingConstraintsFlag', 'unitless', 'Operating outside defined constraints.', {
    suffix: 'ViolatingConstraintsFlag',
  }),
  col('aboveErosionVelocity', 'unitless', 'Above erosion velocity threshold.', { suffix: 'AboveErosionVelocity' }),
  col('erosionalVelocityFlag', 'unitless', 'Erosional velocity limit flag.', { suffix: 'ErosionalVelocityFlag' }),
];

/**
 * The three bare (un-marked) flowline columns on
 * `FlowlineOutputs_Network_Agent` (B3) authored with `prefix:''`
 * (`suffix === canonical`, no rename): the estimated delta-P plus the
 * water-injection / gas-injection connected-manifold ids. Kept separate from
 * {@link FLOWLINE_OUTPUTS} because they carry no `nmv` marker (mixed-prefix
 * source).
 */
export const FLOWLINE_OUTPUTS_BARE: Shape = [
  col('estimatedFlowlineDeltaP', 'pressureDelta', 'Estimated pressure drop across the flowline.'),
  col('connectedManifoldIdWI', 'unitless', 'Connected manifold for the water-injection path.'),
  col('connectedManifoldIdGI', 'unitless', 'Connected manifold for the gas-injection path.'),
];

/**
 * The injection-header measure family on `InjectionHeaderOutputs_Network_Agent`
 * (B3), applied with `prefix:'nmv'` to de-mark the network-matched-value marker
 * (`nmvMeasuredGasInjRate` → `measuredGasInjRate`, …): measured vs estimated
 * gas/water injection rate, pressure and temperature, plus the gas/water
 * injection FMIs.
 */
export const INJECTION_HEADER_OUTPUTS: Shape = [
  col('estimatedGasInjectionPressure', 'pressureAbsolute', 'Estimated gas-injection pressure.', {
    suffix: 'EstimatedGasInjPressure',
  }),
  col('estimatedGasInjectionRate', 'gasRate', 'Estimated gas-injection rate.', { suffix: 'EstimatedGasInjRate' }),
  col('estimatedGasInjectionTemperature', 'temperature', 'Estimated gas-injection temperature.', {
    suffix: 'EstimatedGasInjTemperature',
  }),
  col('estimatedWaterInjectionPressure', 'pressureAbsolute', 'Estimated water-injection pressure.', {
    suffix: 'EstimatedWaterInjPressure',
  }),
  col('estimatedWaterInjectionRate', 'liquidRate', 'Estimated water-injection rate.', {
    suffix: 'EstimatedWaterInjRate',
  }),
  col('estimatedWaterInjectionTemperature', 'temperature', 'Estimated water-injection temperature.', {
    suffix: 'EstimatedWaterInjTemperature',
  }),
  col('gasInjectionFmi', 'unitless', 'Gas-injection flow measurement index.', { suffix: 'GasInjectionFmi' }),
  col('measuredGasInjectionPressure', 'pressureAbsolute', 'Measured gas-injection pressure.', {
    suffix: 'MeasuredGasInjPressure',
  }),
  col('measuredGasInjectionRate', 'gasRate', 'Measured gas-injection rate.', { suffix: 'MeasuredGasInjRate' }),
  col('measuredGasInjectionTemperature', 'temperature', 'Measured gas-injection temperature.', {
    suffix: 'MeasuredGasInjTemperature',
  }),
  col('measuredWaterInjectionPressure', 'pressureAbsolute', 'Measured water-injection pressure.', {
    suffix: 'MeasuredWaterInjPressure',
  }),
  col('measuredWaterInjectionRate', 'liquidRate', 'Measured water-injection rate.', {
    suffix: 'MeasuredWaterInjRate',
  }),
  col('measuredWaterInjectionTemperature', 'temperature', 'Measured water-injection temperature.', {
    suffix: 'MeasuredWaterInjTemperature',
  }),
  col('waterInjectionFmi', 'unitless', 'Water-injection flow measurement index.', { suffix: 'WaterInjectionFmi' }),
];

/**
 * The pump-output measures on `PumpOutputs_Network_Agent` (B3), applied with
 * `prefix:'nmv'` to de-mark the network-matched-value marker
 * (`nmvPumpLiquidRate` → `pumpLiquidRate`). TYPO PRESERVED VERBATIM: the source
 * spells the flag `nmvPumpViolatingConsraintsFlag` ("Consraints", missing `t`),
 * so the suffix MUST be `PumpViolatingConsraintsFlag` and the de-marked
 * canonical carries the typo (`pumpViolatingConsraintsFlag`) — do NOT "fix" it,
 * or the column degrades to unknownCol.
 */
export const PUMP_OUTPUTS_NETWORK: Shape = [
  col('pumpLiquidRate', 'liquidRate', 'Pump liquid throughput rate.', { suffix: 'PumpLiquidRate' }),
  col(
    'pumpViolatingConsraintsFlag',
    'unitless',
    'Pump operating outside defined constraints. Source name preserves the "Consraints" typo.',
    { suffix: 'PumpViolatingConsraintsFlag' },
  ),
];

/**
 * The reservoir-input allocation-factor family on `ReservoirInputs_Agent` (B3):
 * the production and injection per-phase allocation factors. Bare source columns
 * — `suffix === canonical`, `prefix:''`. Authored as a bare passthrough (NOT
 * de-prefixing the `reservoir` lead) to avoid colliding with the grain ref,
 * which is also literally named `reservoir`.
 */
export const RESERVOIR_INPUTS: Shape = [
  col('reservoirInjectionAllocationFactor', 'unitless', 'Reservoir injection allocation factor.'),
  col('reservoirInjectionAllocationGasFactor', 'unitless', 'Reservoir gas-injection allocation factor.'),
  col('reservoirInjectionAllocationOilFactor', 'unitless', 'Reservoir oil-injection allocation factor.'),
  col('reservoirInjectionAllocationWaterFactor', 'unitless', 'Reservoir water-injection allocation factor.'),
  col('reservoirProductionAllocationFactor', 'unitless', 'Reservoir production allocation factor.'),
  col('reservoirProductionAllocationGasFactor', 'unitless', 'Reservoir gas-production allocation factor.'),
  col('reservoirProductionAllocationOilFactor', 'unitless', 'Reservoir oil-production allocation factor.'),
  col('reservoirProductionAllocationWaterFactor', 'unitless', 'Reservoir water-production allocation factor.'),
];
/** Actual field production + injection on `FieldInputs_Agent` (FIELD grain),
 * de-prefixed to clean phase canonicals — volume + rate per phase for production
 * (oil/gas/water) and injection (gas/water). The `ActualOutput` sub-view's
 * measure family. */
export const ACTUAL_OUTPUT: Shape = [
  col('oil', 'liquidVolume', 'Actual field oil-production volume.', { suffix: 'actualOilProduction' }),
  col('oilRate', 'liquidRate', 'Actual field oil-production rate.', { suffix: 'actualOilProductionRate' }),
  col('gas', 'gasVolume', 'Actual field gas-production volume.', { suffix: 'actualGasProduction' }),
  col('gasRate', 'gasRate', 'Actual field gas-production rate.', { suffix: 'actualGasProductionRate' }),
  col('water', 'liquidVolume', 'Actual field water-production volume.', { suffix: 'actualWaterProduction' }),
  col('waterRate', 'liquidRate', 'Actual field water-production rate.', { suffix: 'actualWaterProductionRate' }),
  col('gasInjection', 'gasVolume', 'Actual field gas-injection volume.', { suffix: 'actualGasInjection' }),
  col('gasInjectionRate', 'gasRate', 'Actual field gas-injection rate.', { suffix: 'actualGasInjectionRate' }),
  col('waterInjection', 'liquidVolume', 'Actual field water-injection volume.', { suffix: 'actualWaterInjection' }),
  col('waterInjectionRate', 'liquidRate', 'Actual field water-injection rate.', { suffix: 'actualWaterInjectionRate' }),
];

/** Planning-target field volumes on `FieldInputs_Agent` (FIELD grain) for the
 * `TargetOutput` field-input sub-view. Uses the correct `targetOilProduction`
 * spelling (capital `P`), distinct from the daily-rollup source's typo. */
export const TARGET_OUTPUT_INPUTS: Shape = makeTargetOutput('targetOilProduction');

/** Field gas-composition + ambient inputs on `FieldInputs_Agent` (FIELD grain):
 * associated / non-associated gas production and non-ASR gas injection (volume +
 * rate), plus ambient temperature. The `FieldGasComposition` sub-view's family.
 * These gas streams are distinct, so they keep descriptive canonicals (no shared
 * `gas` short name). */
export const FIELD_GAS_COMPOSITION: Shape = [
  col('associatedGas', 'gasVolumeModel', 'Associated-gas production volume.', { suffix: 'associatedGasProduction' }),
  col('associatedGasRate', 'gasRate', 'Associated-gas production rate.', { suffix: 'associatedGasProductionRate' }),
  col('nonAssociatedGas', 'gasVolumeModel', 'Non-associated-gas production volume.', {
    suffix: 'nonAssociatedGasProduction',
  }),
  col('nonAssociatedGasRate', 'gasRate', 'Non-associated-gas production rate.', {
    suffix: 'nonAssociatedGasProductionRate',
  }),
  col('nonAsrGasInjection', 'gasVolume', 'Non-ASR gas-injection volume.', { suffix: 'nonASRGasInjection' }),
  col('nonAsrGasInjectionRate', 'gasRate', 'Non-ASR gas-injection rate.', { suffix: 'nonASRGasInjectionRate' }),
  col('ambientTemperature', 'temperature', 'Ambient temperature.'),
];

/**
 * The model injectivity/productivity-index + reservoir-pressure sub-family
 * shared byte-for-byte between `WellTestAnalysis_Agent` and
 * `WellTestReservoirAnalysis_Agent` (B3), applied with `prefix:'model'` to
 * de-mark the `model` marker (`modelInjectivityIndex` → `injectivityIndex`, …):
 * the injectivity/productivity indices, the reservoir pressure, and each one's
 * percent-change. The single source of truth for these six columns across both
 * WellTest views.
 */
